import React, { useState } from "react";
import { Routes, Route, Navigate, Outlet, NavLink } from "react-router-dom";
import { toast } from "sonner";
import { SignOut, Key, X, EnvelopeSimple, Phone } from "@phosphor-icons/react";
import { PortalAuthProvider, usePortalAuth } from "@/contexts/PortalAuthContext";
import { portalApi } from "@/lib/portalApi";
import { formatApiError } from "@/lib/api";
import { BRAND, PortalLogo } from "./portalBrand";
import PortalLogin from "./PortalLogin";
import PortalForgotPassword from "./PortalForgotPassword";
import PortalResetPassword from "./PortalResetPassword";
import PortalRequestAccess from "./PortalRequestAccess";
import PortalShipments from "./PortalShipments";
import PortalShipmentDetail from "./PortalShipmentDetail";
import PortalSI from "./PortalSI";
import PortalNotifications from "./PortalNotifications";
import PortalInvoices from "./PortalInvoices";
import PortalRequests from "./PortalRequests";
import PortalBookings from "./PortalBookings";
import PortalQuotes from "./PortalQuotes";
import PortalChat from "./PortalChat";

function ChangePasswordModal({ onClose, forced }) {
  const { refresh } = usePortalAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await portalApi.post("/portal/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated");
      await refresh();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const inp = "w-full h-10 px-3 bg-white border border-slate-300 rounded-md text-sm text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" onClick={forced ? undefined : onClose}>
      <div className="bg-white border border-slate-200 rounded-xl shadow-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="text-slate-900 font-semibold inline-flex items-center gap-2"><Key size={16} className="text-blue-600" /> Change password</div>
          {!forced && <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>}
        </div>
        {forced && <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-3">Please set a new password to continue.</div>}
        <div className="mt-4 space-y-3">
          <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Current / temporary password" className={inp} />
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="New password (min 8 chars)" className={inp} />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          {!forced && <button onClick={onClose} className="px-3 py-2 text-xs text-slate-500 hover:text-slate-800">Cancel</button>}
          <button onClick={submit} disabled={busy} className="px-4 py-2 text-xs font-medium rounded-md bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50">{busy ? "Saving…" : "Update password"}</button>
        </div>
      </div>
    </div>
  );
}

function PortalFooter() {
  return (
    <footer className="border-t border-slate-200 bg-white mt-10">
      <div className="max-w-5xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <PortalLogo className="h-7 w-auto object-contain" fallbackClass="text-sm" />
          <div className="text-[11px] text-slate-500">
            {BRAND.address} · <a href={BRAND.website} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">{BRAND.website.replace(/^https?:\/\//, "")}</a>
          </div>
        </div>
        <div className="flex items-center gap-4 text-[12px] text-slate-600">
          <a href={`mailto:${BRAND.supportEmail}`} className="inline-flex items-center gap-1.5 hover:text-blue-700"><EnvelopeSimple size={14} /> {BRAND.supportEmail}</a>
          <a href={`tel:${BRAND.supportPhone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 hover:text-blue-700"><Phone size={14} /> {BRAND.supportPhone}</a>
        </div>
      </div>
      <div className="text-center text-[10px] text-slate-400 pb-4">© {new Date().getFullYear()} {BRAND.name}. All rights reserved.</div>
    </footer>
  );
}

function PortalLayout() {
  const { customer, logout } = usePortalAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const forced = !!customer?.must_change_password;
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0">
            <PortalLogo className="h-9 w-auto object-contain" />
            <nav className="hidden sm:flex items-center gap-1 border-l border-slate-200 pl-3">
              {[["/portal", "Shipments"], ["/portal/bookings", "Bookings"], ["/portal/quotes", "Quotes"], ["/portal/invoices", "Invoices"], ["/portal/requests", "Request a Quote"], ["/portal/notifications", "Notifications"]].map(([to, label]) => (
                <NavLink key={to} to={to} end={to === "/portal"}
                  className={({ isActive }) => `px-3 py-1.5 rounded-md text-sm font-medium ${isActive ? "bg-blue-50 text-blue-700" : "text-slate-500 hover:text-slate-900"}`}>
                  {label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden sm:inline max-w-[180px] truncate">{customer?.customer_name}</span>
            <button onClick={() => setPwOpen(true)} className="text-xs text-slate-500 hover:text-blue-700 inline-flex items-center gap-1"><Key size={13} /> <span className="hidden sm:inline">Password</span></button>
            <button onClick={logout} data-testid="portal-logout" className="text-xs text-slate-500 hover:text-blue-700 inline-flex items-center gap-1"><SignOut size={13} /> <span className="hidden sm:inline">Sign out</span></button>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-8"><Outlet /></main>
      <PortalFooter />
      {!forced && <PortalChat />}
      {(pwOpen || forced) && <ChangePasswordModal forced={forced} onClose={() => setPwOpen(false)} />}
    </div>
  );
}

function PortalGuard() {
  const { customer, loading } = usePortalAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-400 font-mono text-xs uppercase tracking-widest">Loading…</div>;
  if (!customer) return <Navigate to="/portal/login" replace />;
  return <PortalLayout />;
}

export default function PortalApp() {
  return (
    <PortalAuthProvider>
      <Routes>
        {/* Public (unauthenticated) portal routes — must stay OUTSIDE PortalGuard,
            or a customer following an emailed reset link gets bounced to login. */}
        <Route path="login" element={<PortalLogin />} />
        <Route path="forgot-password" element={<PortalForgotPassword />} />
        <Route path="reset-password" element={<PortalResetPassword />} />
        <Route path="request-access" element={<PortalRequestAccess />} />
        <Route element={<PortalGuard />}>
          <Route path="" element={<PortalShipments />} />
          <Route path="shipments/:id" element={<PortalShipmentDetail />} />
          <Route path="shipments/:id/si" element={<PortalSI />} />
          <Route path="bookings" element={<PortalBookings />} />
          <Route path="quotes" element={<PortalQuotes />} />
          <Route path="invoices" element={<PortalInvoices />} />
          <Route path="requests" element={<PortalRequests />} />
          <Route path="notifications" element={<PortalNotifications />} />
        </Route>
        <Route path="*" element={<Navigate to="/portal" replace />} />
      </Routes>
    </PortalAuthProvider>
  );
}
